import { Children, forwardRef, isValidElement, useContext, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link, UNSAFE_DataRouterContext, type LinkProps } from "react-router";
import { cn } from "~/lib/cn";
import { startsInAppNavigation, useNavPendingTo } from "~/components/layout/use-nav-pending";
import { ActionFormContext } from "./ActionForm";

const dsVariantClasses = {
  // Compatibility names resolve to the same atoms, never a parallel style system.
  primary:       "btn-dark",
  outline:       "btn-ghost",
  dark:          "btn-dark",
  ghost:         "btn-ghost",
  destructive:   "btn-destructive",
  signature:     "btn-signature",
  gradient:      "btn-gradient",
  "pill-solid":  "btn-pill-solid",
  "pill-dark":   "btn-pill-dark",
  "pill-outline": "btn-pill-outline",
  "pill-outline-light": "btn-pill-outline-light",
  tool:          "btn-tool",
  "text":        "btn-text",
  "text-on-dark": "btn-text on-dark",
  // The delivered mono text action (`recorrido-oct.css` `.rf-textbtn.is-strong`): the save
  // notice's «Comprobar» / «Reintentar» keep the notice's technical voice.
  "text-mono":   "btn-text is-mono",
  "pill-action": "btn-pill-action",
  "round-ctrl":  "btn-round-ctrl",
  "outline-on-dark": "btn-outline-on-dark",
  huge:          "btn-huge",
  icon:          "icon-btn",
  // T872 S2 — the public surfaces' action pair (October delivery, tfc-actions.css):
  // `public-primary` = pink solid, one per section; `public-secondary` = thin
  // currentColor outline, same geometry. Modifiers via className: `.is-compact`
  // (44px), `.is-full` (block width), `.on-dark` (ink and focus on a dark band).
  "public-primary":   "btn-public-primary",
  "public-secondary": "btn-public-secondary",
  // Dark-hero CTAs keep their palette within the shared action geometry.
  // `hero-accent` = the product-themed `.btn-cta` atom (carmín core / blue sculpt /
  // gold add-on per `data-ritual`, with per-product ink); `hero` = neutral action.
  hero:          "cta-d",
  "hero-accent": "btn-cta",
  // Escape hatch for bespoke-surface controls (e.g. the member library's
  // .bib-chip / .ses-fav / .bibm-* controls): renders a class-free button
  // element carrying ONLY the consumer's className, so a surface that owns
  // its full button styling in app.css still routes through the SSOT
  // primitive (R14) instead of hand-rolling raw markup. No action geometry, no
  // variant class.
  unstyled:      "",
} as const;


export type ButtonVariant = keyof typeof dsVariantClasses;
type Size = "sm" | "md" | "lg" | "readable";
const structural = new Set<ButtonVariant>(["icon", "round-ctrl", "pill-solid", "pill-dark", "pill-outline", "pill-outline-light", "unstyled"]);

interface SharedProps {
  variant?: ButtonVariant;
  size?: Size;
  surface?: "light" | "dark";
  /** @deprecated Sizes now always share the action contract. */
  applySize?: boolean;
  /** Request owner supplies the entire pending interval, including revalidation. */
  isLoading?: boolean;
  /** Aria mode retains focus during OAuth handoff; activation is still guarded. */
  disabledMode?: "native" | "aria";
  locked?: boolean;
  lockLabel?: string;
  /** What the wait is, for assistive tech («Entrando…»). The visible label never changes
   *  (stable geometry); this is announced through a polite status region while busy. */
  busyLabel?: string;
  className?: string;
  trailingIcon?: ReactNode;
  children?: ReactNode;
}
type ButtonAsButton = SharedProps & Omit<React.ComponentProps<"button">, keyof SharedProps> & { href?: never };
type ButtonAsLink = SharedProps & Omit<LinkProps, keyof SharedProps | "to"> & { href: string; navPending?: boolean };
type IconA11yConstraint = { variant: ButtonVariant; "aria-label": string } | { variant?: Exclude<ButtonVariant, "icon"> };
export type ButtonProps = (ButtonAsButton | ButtonAsLink) & IconA11yConstraint;

function NavigationButton({ to, render }: { to: string; render: (pending: boolean, start: () => void) => React.ReactElement }) {
  const { pending, onNavigate } = useNavPendingTo(to);
  return render(pending, onNavigate);
}

function LockIcon() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="5" y="11" width="14" height="9" rx="1" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>;
}

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(function Button(props, ref) {
  const formSubmission = useContext(ActionFormContext);
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  const elementRef = useRef<HTMLButtonElement | HTMLAnchorElement | null>(null);
  const labelId = useId();
  // A `reloadDocument` link is a full page load the router never sees: it owns its own
  // wait from the tap until the page unloads; a back/forward-cache restore releases it.
  const reloads = "href" in props && Boolean((props as { reloadDocument?: boolean }).reloadDocument);
  const [leavingDocument, setLeavingDocument] = useState(false);
  useEffect(() => {
    if (!reloads) return;
    const release = () => setLeavingDocument(false);
    window.addEventListener("pageshow", release);
    return () => window.removeEventListener("pageshow", release);
  }, [reloads]);
  const reasonId = useId();
  const {
    variant = "primary", size = "md", surface, applySize: _applySize,
    isLoading = false, disabledMode = "native", locked = false, lockLabel, busyLabel, className, trailingIcon, children, ...rest
  } = props;
  const action = !structural.has(variant);
  const isSubmit = !("href" in props) && props.type === "submit";
  // An explicit form association wins over DOM ancestry, including context ancestry.
  const associatedElsewhere = !("href" in props) && Boolean(props.form);
  // Siblings wait only behind a write; a newer GET filter may replace an older one.
  const formPending = isSubmit && !associatedElsewhere && Boolean(formSubmission?.pending);
  const siblingBlocked = formPending && Boolean(formSubmission?.mutation);
  const ownSubmission = formPending && formSubmission?.submitter === elementRef.current;
  const busy = isLoading || ownSubmission;
  const reservePending = props.isLoading !== undefined || (isSubmit && !associatedElsewhere && formSubmission !== null);
  const classes = cn(
    dsVariantClasses[variant],
    action && "btn-action",
    action && `btn-size-${size === "readable" ? "md" : size}`,
    action && size === "readable" && "btn-size-readable",
    variant !== "unstyled" && surface && `on-${surface}`,
    locked && !busy && "is-locked",
    className,
  );
  const setRef = (element: HTMLButtonElement | HTMLAnchorElement | null) => {
    elementRef.current = element;
    if (typeof ref === "function") ref(element);
    else if (ref) ref.current = element;
  };

  // Existing decorative trailing arrows move into the shared accessory slot.
  // Structural tiles/tabs keep their arbitrary markup intact.
  const nodes = action ? Children.toArray(children) : [];
  const last = nodes.at(-1);
  const trailing = trailingIcon ?? (action && isValidElement<{ "aria-hidden"?: boolean | "true" }>(last)
    && (last.props["aria-hidden"] === true || last.props["aria-hidden"] === "true") ? nodes.pop() : null);
  const content = (pending: boolean, navigates = false) => {
    const showLock = locked && !pending;
    const slot = reservePending || navigates || trailing || showLock;
    // A slot reserved only for the ring is balanced by an equal leading spacer, so the
    // label stays centred whether or not the ring is showing (geometry never moves).
    // Text-shaped actions (text, text-on-dark, text-mono) start flush with their line.
    const balance = slot && !trailing && !dsVariantClasses[variant].includes("btn-text");
    const indicator = pending ? <span className="tfc-pending-ring btn-pending-ring" /> : showLock ? <LockIcon /> : trailing;
    if (!action) {
      if ((variant === "icon" || variant === "round-ctrl") && (reservePending || navigates)) return <span className="btn-icon-content"><span style={{ visibility: pending ? "hidden" : undefined }}>{children}</span>{pending && <span className="btn-icon-pending" aria-hidden="true">{indicator}</span>}</span>;
      return <>{children}{slot && <span className="btn-pending-slot" aria-hidden="true">{indicator}</span>}</>;
    }
    return <>{balance && <span className="btn-slot-balance" aria-hidden="true" />}<span id={labelId} className="btn-label">{nodes}</span>{slot && <span className="btn-pending-slot" aria-hidden="true">{indicator}</span>}</>;
  };
  const lockReason = locked && lockLabel ? <span id={reasonId} className="sr-only">{lockLabel}</span> : null;
  // The region exists before the wait starts, so its change is announced.
  const status = (waiting: boolean) => busyLabel ? <span role="status" className="sr-only">{waiting ? busyLabel : ""}</span> : null;
  const explanation = (waiting: boolean) => <>{lockReason}{status(waiting)}</>;
  const describedBy = cn(rest["aria-describedby"], locked && lockLabel && reasonId) || undefined;

  if ("href" in rest && rest.href != null) {
    const { href, navPending, onClick, onKeyDown, ...linkRest } = rest as ButtonAsLink;
    const tracksNavigation = !reloads && (navPending ?? Boolean(dataRouter));
    const render = (pending: boolean, start?: () => void) => {
      const waiting = busy || pending || leavingDocument;
      const unavailable = waiting || locked || linkRest["aria-disabled"] === true || linkRest["aria-disabled"] === "true";
      return <><Link
        {...linkRest} ref={setRef} to={href} className={classes}
        aria-busy={waiting || undefined} aria-disabled={unavailable || undefined}
        aria-describedby={describedBy} title={locked ? lockLabel : linkRest.title}
        onClick={event => {
          if (unavailable) { event.preventDefault(); return; }
          onClick?.(event);
          if (startsInAppNavigation(event)) {
            if (reloads) setLeavingDocument(true);
            else start?.();
          }
        }}
        onKeyDown={event => {
          if (unavailable && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); return; }
          onKeyDown?.(event);
        }}
      >{content(waiting, tracksNavigation || reloads)}</Link>{explanation(waiting)}</>;
    };
    return tracksNavigation ? <NavigationButton to={href} render={render} /> : render(false);
  }

  const { onClick, onKeyDown, disabled, title, type = "button", ...buttonRest } = rest as ButtonAsButton;
  const waiting = busy || siblingBlocked;
  const unavailable = waiting || locked || disabled || buttonRest["aria-disabled"] === true || buttonRest["aria-disabled"] === "true";
  // A wait never uses native `disabled`: that would drop focus to <body> mid-request
  // (R-T972-2). Activation is refused in the handlers below instead.
  return <><button
    {...buttonRest} ref={setRef} type={type} className={classes}
    // A consumer's `disabled={busy}` must not bring native disabling back mid-wait.
    disabled={disabledMode === "native" ? disabled && !busy : undefined}
    aria-busy={busy || buttonRest["aria-busy"] || undefined}
    aria-disabled={locked || waiting || (disabledMode === "aria" && unavailable) || buttonRest["aria-disabled"] || undefined}
    aria-describedby={describedBy} title={locked ? lockLabel : title}
    onClick={event => { if (unavailable) { event.preventDefault(); return; } onClick?.(event); }}
    onKeyDown={event => {
      if (unavailable && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); return; }
      onKeyDown?.(event);
    }}
  >{content(busy)}</button>{explanation(busy)}</>;
});
